<template>
  <HrPageLayout
    title="County Secretary — Overview"
    subtitle="Cabinet, correspondences and secretary operations dashboard"
    icon="📑"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'County Secretary' },
      { label: 'Overview' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-primary" :disabled="loading" @click="loadAll">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
      </button>
    </template>

    <div class="cs-wrap">
      <div v-if="error" class="hr-alert hr-alert-danger">
        {{ error }}
        <button type="button" class="hr-btn hr-btn-sm mt-2" @click="loadAll">Retry</button>
      </div>

      <div class="metrics">
        <div class="metric m1"><span>Cabinet meetings</span><strong>{{ stats.meetings }}</strong></div>
        <div class="metric m2"><span>Cabinet papers</span><strong>{{ stats.papers }}</strong></div>
        <div class="metric m3"><span>External communications</span><strong>{{ stats.external }}</strong></div>
        <div class="metric m4"><span>Cabinet documents</span><strong>{{ stats.documents }}</strong></div>
        <div class="metric m5"><span>Correspondences</span><strong>{{ stats.hrKnowledge }}</strong></div>
        <div class="metric m6"><span>CS correspondences</span><strong>{{ stats.csCorr }}</strong></div>
      </div>

      <div class="grid-2">
        <div class="panel">
          <div class="panel-h"><h4>Module shortcuts</h4></div>
          <div class="links">
            <router-link :to="{ name: 'module-cabinet' }" class="link-card">
              <i class="bx bx-buildings"></i>
              <div>
                <strong>Cabinet</strong>
                <small>Meetings, papers and documents</small>
              </div>
            </router-link>
            <router-link :to="{ name: 'module-hr-knowledge' }" class="link-card">
              <i class="bx bx-book-open"></i>
              <div>
                <strong>Correspondences</strong>
                <small>Knowledge / correspondence register</small>
              </div>
            </router-link>
            <router-link :to="{ name: 'module-cs-correspondences' }" class="link-card">
              <i class="bx bx-mail-send"></i>
              <div>
                <strong>Correspondences from CS</strong>
                <small>Secretary-issued correspondence</small>
              </div>
            </router-link>
          </div>
        </div>

        <div class="panel">
          <div class="panel-h">
            <h4>Recent cabinet meetings</h4>
            <router-link :to="{ name: 'module-cabinet' }">Open cabinet</router-link>
          </div>
          <table class="tbl">
            <thead>
              <tr><th>Title / Ref</th><th>Date</th><th>Status</th></tr>
            </thead>
            <tbody>
              <tr v-for="(m, i) in recentMeetings" :key="'m' + i">
                <td>{{ m.title || m.meeting_title || m.ref_no || 'Meeting' }}</td>
                <td>{{ m.meeting_date || m.date || m.created_at || '—' }}</td>
                <td>{{ m.status || '—' }}</td>
              </tr>
              <tr v-if="!recentMeetings.length">
                <td colspan="3" class="muted">{{ loading ? 'Loading…' : 'No meetings found.' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="panel">
        <div class="panel-h">
          <h4>Recent correspondences</h4>
          <router-link :to="{ name: 'module-hr-knowledge' }">View all</router-link>
        </div>
        <table class="tbl">
          <thead>
            <tr><th>Subject / Title</th><th>Date</th><th>Status</th></tr>
          </thead>
          <tbody>
            <tr v-for="(d, i) in recentCorr" :key="'c' + i">
              <td>{{ d.title || d.subject || d.document_title || 'Item' }}</td>
              <td>{{ d.document_date || d.created_at || d.date || '—' }}</td>
              <td>{{ d.status || d.workflow_status || '—' }}</td>
            </tr>
            <tr v-if="!recentCorr.length">
              <td colspan="3" class="muted">{{ loading ? 'Loading…' : 'No correspondence items loaded.' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

function asList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.list)) return payload.list;
  if (Array.isArray(payload.meetings)) return payload.meetings;
  if (Array.isArray(payload.papers)) return payload.papers;
  if (Array.isArray(payload.documents)) return payload.documents;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
}

export default {
  name: "CountySecretaryDashboard",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      error: "",
      stats: {
        meetings: 0,
        papers: 0,
        external: 0,
        documents: 0,
        hrKnowledge: 0,
        csCorr: 0
      },
      recentMeetings: [],
      recentCorr: []
    };
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    async post(fn) {
      const fd = new FormData();
      fd.append("function", fn);
      return execute(fd, authUrl);
    },
    countFrom(res) {
      if (!res?.data?.success) return 0;
      const d = res.data.data;
      if (typeof d?.total === "number") return d.total;
      if (typeof d?.pagination?.total === "number") return d.pagination.total;
      return asList(d).length;
    },
    async loadAll() {
      this.loading = true;
      this.error = "";
      try {
        const [meetRes, paperRes, extRes, docRes, hrRes, csRes] = await Promise.all([
          this.post("getCabinetMeetingList"),
          this.post("getCabinetPaperList"),
          this.post("getCabinetExternalCommList"),
          this.post("getCabinetDocumentList"),
          this.post("getHrKnowledgeDocumentList"),
          this.post("getCsCorrespondenceList")
        ]);

        this.stats.meetings = this.countFrom(meetRes);
        this.stats.papers = this.countFrom(paperRes);
        this.stats.external = this.countFrom(extRes);
        this.stats.documents = this.countFrom(docRes);
        this.stats.hrKnowledge = this.countFrom(hrRes);
        this.stats.csCorr = this.countFrom(csRes);

        this.recentMeetings = asList(meetRes.data?.data).slice(0, 8);
        this.recentCorr = asList(hrRes.data?.data).slice(0, 8);
      } catch (e) {
        this.error = "Could not load County Secretary overview.";
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.cs-wrap { display: flex; flex-direction: column; gap: 16px; }
.metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}
.metric {
  border-radius: 14px;
  padding: 16px;
  color: #fff;
}
.metric span { display: block; font-size: 12px; opacity: 0.9; margin-bottom: 6px; }
.metric strong { font-size: 1.6rem; }
.m1 { background: linear-gradient(135deg, #1e293b, #0f172a); }
.m2 { background: linear-gradient(135deg, #0c2d48, #164e63); }
.m3 { background: linear-gradient(135deg, #0e7490, #155e75); }
.m4 { background: linear-gradient(135deg, #3f6212, #4d7c0f); }
.m5 { background: linear-gradient(135deg, #7c2d12, #9a3412); }
.m6 { background: linear-gradient(135deg, #1e3a8a, #1d4ed8); }
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 14px;
}
@media (max-width: 900px) {
  .grid-2 { grid-template-columns: 1fr; }
}
.panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 16px;
}
.panel-h {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.panel-h h4 { margin: 0; color: #0c2d48; }
.panel-h a { font-size: 13px; color: #0e7490; text-decoration: none; font-weight: 600; }
.links { display: flex; flex-direction: column; gap: 10px; }
.link-card {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 14px;
  border-radius: 12px;
  background: linear-gradient(180deg, #f8fafc, #fff);
  border: 1px solid #e2e8f0;
  color: inherit;
  text-decoration: none;
}
.link-card:hover { border-color: #0e7490; }
.link-card i { font-size: 22px; color: #0c2d48; }
.link-card strong { display: block; color: #0f172a; }
.link-card small { color: #64748b; }
.tbl { width: 100%; border-collapse: collapse; font-size: 13px; }
.tbl th, .tbl td { padding: 8px 6px; border-bottom: 1px solid #f1f5f9; text-align: left; }
.tbl th { color: #64748b; font-size: 12px; }
.muted { color: #94a3b8; text-align: center; padding: 12px; }
.mt-2 { margin-top: 8px; }
</style>
